// src/beta/components/DocumentModal.tsx
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getDocument } from '@/beta/services/documents';

interface Props {
  docId: string;
  onClose: () => void;
}

function fileNameFromUrl(url: string): string {
  try {
    const u = new URL(url);
    return u.pathname.split('/').pop() || url;
  } catch {
    return url.split('/').pop() || url;
  }
}

/** § 6.4 (4) modal: full-screen document viewer.
 *  Phase F renders title + filename + iframe at file_url. Full PDF.js
 *  rendering is deferred to a later phase. Closes on ESC, backdrop, or button. */
export function DocumentModal({ docId, onClose }: Props) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['beta', 'document', docId],
    queryFn: () => getDocument(docId),
    staleTime: 5 * 60 * 1000,
  });

  // ESC handler
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      data-testid="document-modal-backdrop"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.92)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        data-testid="document-modal-panel"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(960px, 92vw)',
          height: 'min(720px, 88vh)',
          background: 'var(--vm-dark-card)',
          border: '1px solid var(--vm-border)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <header
          style={{
            padding: 'var(--vm-s-3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid var(--vm-border)',
          }}
        >
          <div>
            <div
              style={{
                fontFamily: 'var(--vm-font-heading)',
                fontSize: 18,
                color: 'var(--vm-warm-white)',
              }}
            >
              {isLoading ? 'Loading…' : isError ? 'Could not load document' : data?.title}
            </div>
            <div
              style={{
                fontFamily: 'var(--vm-font-heading)',
                fontSize: 10,
                letterSpacing: '0.3em',
                textTransform: 'uppercase',
                color: 'var(--vm-stone)',
              }}
            >
              {data?.file_url ? fileNameFromUrl(data.file_url) : ''}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent',
              color: 'var(--vm-warm-white)',
              border: '1px solid var(--vm-border)',
              padding: '6px 14px',
              fontFamily: 'var(--vm-font-heading)',
              fontSize: 11,
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </header>

        <div style={{ flex: 1, minHeight: 0, background: '#000' }}>
          {data?.file_url ? (
            <iframe
              data-testid="document-modal-iframe"
              src={data.file_url}
              title={data.title}
              style={{ width: '100%', height: '100%', border: 0 }}
            />
          ) : (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--vm-stone)',
                fontFamily: 'var(--vm-font-heading)',
                fontSize: 11,
                letterSpacing: '0.3em',
                textTransform: 'uppercase',
              }}
            >
              {isLoading ? 'Loading…' : 'File unavailable'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
